import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './AddTransportPerson.css';
import './SchoolDashboard.css';

const AddTransportPerson = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schoolData, setSchoolData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId } = useBranchFilter();
  const [formData, setFormData] = useState({
    firstName: '',
    middleName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    car_number: '',
    phone: '',
    license_number: '',
    address: '',
    country: 'Kenya',
    city: '',
    postalCode: '',
    nationalId: '',
    employeeId: '',
    profilePicture: null
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Country code state
  const [transportPhoneCountryCode, setTransportPhoneCountryCode] = useState('+254'); // Default: Kenya
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // Branch state
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [formBranchId, setFormBranchId] = useState(null); // Branch selected in form when "All Branches" is selected

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  // Fetch branches when school data is available
  useEffect(() => {
    const fetchBranches = async () => {
      if (!schoolData?.id) return;
      
      try {
        setLoadingBranches(true);
        const { data, error } = await supabase
          .from('branches')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .order('branch_name');
        
        if (error) {
          console.error('Error fetching branches:', error);
          setBranches([]);
        } else {
          setBranches(data || []);
        }
      } catch (err) {
        console.error('Error:', err);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (schoolData?.id) {
      fetchBranches();
    }
  }, [schoolData?.id]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.car_number.trim()) {
      newErrors.car_number = 'Car number is required';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.country.trim()) {
      newErrors.country = 'Country is required';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'City/Town is required';
    }

    if (!formData.postalCode.trim()) {
      newErrors.postalCode = 'Postal Code is required';
    }

    // Validate branch selection when "All Branches" is selected
    if (!selectedBranchId && branches.length > 0 && !formBranchId) {
      newErrors.branchId = 'Branch selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Generate password function: word + numbers (minimum 6 characters total)
  const generatePassword = () => {
    const words = ['book', 'star', 'moon', 'tree', 'bird', 'fish', 'rock', 'wind', 'fire', 'wave', 'lake', 'hill', 'rose', 'leaf', 'snow', 'rain', 'sun', 'sky', 'sea', 'gem'];
    const randomWord = words[Math.floor(Math.random() * words.length)];
    const wordLength = randomWord.length;
    
    // Calculate how many digits we need to ensure minimum 6 characters total
    // If word is 3 chars, need 3 digits (total 6)
    // If word is 4 chars, need 2 digits (total 6)
    // If word is 5+ chars, need 1 digit (total 6+)
    const minDigitsNeeded = Math.max(1, 6 - wordLength);
    
    // Generate random number with appropriate number of digits
    // For 1 digit: 0-9
    // For 2 digits: 10-99
    // For 3 digits: 100-999
    const maxNumber = Math.pow(10, minDigitsNeeded) - 1;
    const minNumber = minDigitsNeeded === 1 ? 0 : Math.pow(10, minDigitsNeeded - 1);
    const randomNumber = Math.floor(Math.random() * (maxNumber - minNumber + 1)) + minNumber;
    const randomDigits = randomNumber.toString().padStart(minDigitsNeeded, '0');
    
    return randomWord + randomDigits;
  };

  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size should be less than 5MB');
      return;
    }

    setFormData(prev => ({
      ...prev,
      profilePicture: file
    }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfilePicturePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const removeProfilePicture = () => {
    setFormData(prev => ({
      ...prev,
      profilePicture: null
    }));
    setProfilePicturePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Upload profile picture if provided
      let profilePictureUrl = null;
      if (formData.profilePicture) {
        try {
          setUploadingPicture(true);
          console.log('Starting profile picture upload...');
          console.log('File:', formData.profilePicture.name, 'Size:', formData.profilePicture.size);
          
          const fileExt = formData.profilePicture.name.split('.').pop();
          const fileName = `transport_${Date.now()}.${fileExt}`;
          const filePath = `transport-profiles/${schoolData.id}/${fileName}`;
          
          console.log('Uploading to path:', filePath);
          console.log('Bucket: transport-uploads');

          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('transport-uploads')
            .upload(filePath, formData.profilePicture, {
              cacheControl: '3600',
              upsert: false
            });

          if (uploadError) {
            console.error('Error uploading profile picture:', uploadError);
            console.error('Upload error details:', JSON.stringify(uploadError, null, 2));
            alert(`Failed to upload profile picture: ${uploadError.message}. The driver will be added without a profile picture.`);
            // Continue without profile picture if upload fails
          } else {
            console.log('Profile picture uploaded successfully:', uploadData);
            
            // Get public URL
            const { data: urlData } = supabase.storage
              .from('transport-uploads')
              .getPublicUrl(filePath);
            
            profilePictureUrl = urlData.publicUrl;
            console.log('Profile picture URL generated:', profilePictureUrl);
            
            if (!profilePictureUrl) {
              console.error('Failed to generate profile picture URL');
              alert('Profile picture uploaded but URL generation failed. The driver will be added without a profile picture.');
            }
          }
        } catch (uploadErr) {
          console.error('Exception during profile picture upload:', uploadErr);
          alert(`Error uploading profile picture: ${uploadErr.message}. The driver will be added without a profile picture.`);
          // Continue without profile picture if upload fails
        } finally {
          setUploadingPicture(false);
        }
      } else {
        console.log('No profile picture provided');
      }
      
      console.log('Final profilePictureUrl before saving:', profilePictureUrl);

      // Combine first, middle, and last name
      const fullName = [formData.firstName, formData.middleName, formData.lastName]
        .filter(name => name.trim() !== '')
        .join(' ');

      // First, create user in Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: fullName,
            user_type: 'transport_person',
            school_id: schoolData.id
          }
        }
      });

      if (authError) {
        console.error('Auth error:', authError);
          alert(`Failed to create driver account: ${authError.message}`);
        setIsSubmitting(false);
        return;
      }

      if (authData.user) {
        console.log('Auth user created:', authData.user);
        
        // Wait a moment for the auth user to be fully created
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Use formBranchId if available, otherwise fallback to selectedBranchId
        const finalBranchId = formBranchId || selectedBranchId || null;
        
        // Insert transport person details into transport_persons table
        const driverData = {
          id: authData.user.id, // Use the auth user ID
          school_id: schoolData.id,
          branch_id: finalBranchId, // Include branch_id if a branch is selected
          name: fullName,
          first_name: formData.firstName.trim(),
          middle_name: formData.middleName.trim() || null,
          last_name: formData.lastName.trim(),
          email: formData.email,
          car_number: formData.car_number,
          phone: formData.phone.trim() ? `${transportPhoneCountryCode} ${formData.phone.trim()}` : '',
          license_number: formData.license_number || null,
          address: formData.address || null,
          country: formData.country.trim(),
          city: formData.city.trim(),
          postal_code: formData.postalCode.trim(),
          national_id: formData.nationalId.trim() || null,
          employee_id: formData.employeeId.trim() || null,
          profile_picture_url: profilePictureUrl,
          is_active: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        console.log('Saving driver data to database:', {
          ...driverData,
          profile_picture_url: profilePictureUrl ? 'URL present' : 'NULL'
        });
        
        const { data: insertData, error: dbError } = await supabase
          .from('transport_persons')
          .insert(driverData)
          .select();

        if (dbError) {
          console.error('Database error:', dbError);
          alert(`Failed to save driver details: ${dbError.message}`);
          setIsSubmitting(false);
          return;
        }

        console.log('Transport person created:', insertData);
        console.log('School ID used:', schoolData.id);
        console.log('Auth user ID:', authData.user.id);
        
        // Reset form
        setFormData({
          firstName: '',
          middleName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
          car_number: '',
          phone: '',
          license_number: '',
          address: '',
          country: 'Kenya',
          city: '',
          postalCode: '',
          nationalId: '',
          employeeId: '',
          profilePicture: null
        });
        setProfilePicturePreview(null);
        setFormBranchId(null); // Reset branch selection
        setErrors({});
        
        // Show success popup
        setSuccessData({
          email: formData.email,
          password: formData.password,
          name: fullName,
          employeeId: formData.employeeId || 'N/A'
        });
        setShowSuccessPopup(true);
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to add driver. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the school dashboard.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>🚌 Add Driver</h1>
          <p>Add a new driver to your school system</p>
        </div>

        <div className="add-transport-person-container">
          <div className="section-card">
            <h2>Driver Details</h2>
            <p className="section-description">
              Fill in the details below to add a new driver. They will receive login credentials.
            </p>
            
            <form onSubmit={handleSubmit} className="transport-person-form">
              {/* Branch Selection */}
              {!selectedBranchId && branches.length > 0 && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="formBranchId">Branch *</label>
                    <select
                      id="formBranchId"
                      name="formBranchId"
                      value={formBranchId || ''}
                      onChange={(e) => setFormBranchId(e.target.value ? parseInt(e.target.value) : null)}
                      className={`form-input ${errors.branchId ? 'error' : ''}`}
                    >
                      <option value="">Select Branch</option>
                      {branches.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.branch_name}
                        </option>
                      ))}
                    </select>
                    {errors.branchId && <span className="error-message">{errors.branchId}</span>}
                    <small style={{ display: 'block', marginTop: '0.25rem', color: '#6B7280', fontSize: '0.75rem' }}>
                      Transport persons cannot be added to "All Branches". Please select a specific branch.
                    </small>
                  </div>
                </div>
              )}
              
              <div className="form-row three-columns">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`form-input ${errors.firstName ? 'error' : ''}`}
                    placeholder="Enter first name"
                    required
                  />
                  {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="middleName">Middle Name</label>
                  <input
                    type="text"
                    id="middleName"
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter middle name (optional)"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`form-input ${errors.lastName ? 'error' : ''}`}
                    placeholder="Enter last name"
                    required
                  />
                  {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="employeeId">Employee ID</label>
                  <input
                    type="text"
                    id="employeeId"
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter employee ID (optional)"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    placeholder="Enter email address"
                    required
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>

              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', alignItems: 'start' }}>
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                    Profile Picture
                  </label>
                  <div style={{
                    position: 'relative',
                    marginTop: '0.5rem',
                    width: '100%'
                  }}>
                    <input
                      type="file"
                      id="profilePicture"
                      name="profilePicture"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      style={{
                        position: 'absolute',
                        width: '0.1px',
                        height: '0.1px',
                        opacity: 0,
                        overflow: 'hidden',
                        zIndex: -1,
                        pointerEvents: 'none'
                      }}
                    />
                    <label
                      htmlFor="profilePicture"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '0.4rem 0.6rem',
                        background: '#FFFFFF',
                        color: '#374151',
                        border: '1px solid #D1D5DB',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.8rem',
                        fontWeight: '400',
                        transition: 'all 0.2s ease',
                        width: '100%',
                        textAlign: 'center',
                        gap: '0.5rem',
                        userSelect: 'none',
                        boxSizing: 'border-box'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#F9FAFB';
                        e.currentTarget.style.borderColor = '#9CA3AF';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#FFFFFF';
                        e.currentTarget.style.borderColor = '#D1D5DB';
                      }}
                    >
                      <span style={{ fontSize: '0.9rem' }}>📷</span>
                      <span>{formData.profilePicture ? 'Change' : 'Choose File'}</span>
                    </label>
                  </div>
                  {(formData.profilePicture || profilePicturePreview) && (
                    <div style={{
                      marginTop: '1rem',
                      padding: '1rem',
                      background: '#F9FAFB',
                      borderRadius: '8px',
                      border: '1px solid #E5E7EB',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '3px solid #FF8C42',
                        flexShrink: 0
                      }}>
                        {profilePicturePreview ? (
                          <img 
                            src={profilePicturePreview} 
                            alt="Profile preview" 
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            background: '#E5E7EB',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '2rem'
                          }}>
                            👤
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        {formData.profilePicture && (
                          <>
                            <div style={{ 
                              fontWeight: '600', 
                              color: '#1F2937', 
                              marginBottom: '0.25rem', 
                              wordBreak: 'break-word',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {formData.profilePicture.name}
                            </div>
                            <div style={{ fontSize: '0.85rem', color: '#6B7280', whiteSpace: 'nowrap' }}>
                              {(formData.profilePicture.size / 1024 / 1024).toFixed(2)} MB
                            </div>
                          </>
                        )}
                      </div>
                      {formData.profilePicture && (
                        <button
                          type="button"
                          onClick={removeProfilePicture}
                          style={{
                            padding: '0.4rem 0.6rem',
                            background: '#EF4444',
                            color: '#FFFFFF',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '0.8rem',
                            fontWeight: '500',
                            transition: 'all 0.2s ease',
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            width: 'auto',
                            height: 'auto',
                            lineHeight: '1.2'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#DC2626';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#EF4444';
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  )}
                  <small style={{ 
                    display: 'block', 
                    marginTop: '0.5rem', 
                    color: '#6B7280', 
                    fontSize: '0.85rem',
                    fontStyle: 'italic'
                  }}>
                    Max size: 5MB. Accepted formats: JPG, PNG, GIF
                  </small>
                </div>
                
                <div className="form-group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <label htmlFor="nationalId" style={{ marginBottom: '0.5rem', fontWeight: '500', color: '#374151' }}>
                    National/Government ID
                  </label>
                  <input
                    type="text"
                    id="nationalId"
                    name="nationalId"
                    value={formData.nationalId}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter National/Government ID (optional)"
                    style={{
                      width: '100%',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <div className="password-input-wrapper" style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`form-input ${errors.password ? 'error' : ''}`}
                      placeholder="Enter password"
                      required
                      style={{ paddingRight: '2.5rem' }}
                    />
                    <span 
                      className="password-toggle-icon"
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? "Hide password" : "Show password"}
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        userSelect: 'none',
                        zIndex: 1
                      }}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </span>
                  </div>
                  {errors.password && <span className="error-message">{errors.password}</span>}
                  <div style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      className="toggle-btn"
                      onClick={() => {
                        const genPassword = generatePassword();
                        setFormData(prev => ({
                          ...prev,
                          password: genPassword,
                          confirmPassword: genPassword
                        }));
                      }}
                      style={{
                        padding: '0.4rem 0.8rem',
                        backgroundColor: '#10B981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '0.85rem'
                      }}
                    >
                      Generate Password (min 6 characters)
                    </button>
                    <small style={{ display: 'block', marginTop: '0.25rem', color: '#6B7280', fontSize: '0.75rem' }}>
                      Remember: Password must be at least 6 characters
                    </small>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password *</label>
                  <div className="password-input-wrapper" style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                      placeholder="Confirm password"
                      required
                      style={{ paddingRight: '2.5rem' }}
                    />
                    <span 
                      className="password-toggle-icon"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      title={showConfirmPassword ? "Hide password" : "Show password"}
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        userSelect: 'none',
                        zIndex: 1
                      }}
                    >
                      {showConfirmPassword ? '🙈' : '👁️'}
                    </span>
                  </div>
                  {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="car_number">Car Number *</label>
                  <input
                    type="text"
                    id="car_number"
                    name="car_number"
                    value={formData.car_number}
                    onChange={handleInputChange}
                    className={`form-input ${errors.car_number ? 'error' : ''}`}
                    placeholder="Enter car registration number"
                    required
                  />
                  {errors.car_number && <span className="error-message">{errors.car_number}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <div className="phone-input-wrapper">
                    <select
                      className="country-code-select"
                      value={transportPhoneCountryCode}
                      onChange={(e) => setTransportPhoneCountryCode(e.target.value)}
                      style={{ 
                        width: '90px', 
                        minWidth: '90px', 
                        maxWidth: '110px',
                        padding: '0.75rem 0.5rem',
                        fontSize: '0.85rem'
                      }}
                    >
                      <option value="+1">🇺🇸 +1 (US/CA)</option>
                      <option value="+44">🇬🇧 +44 (UK)</option>
                      <option value="+91">🇮🇳 +91 (India)</option>
                      <option value="+92">🇵🇰 +92 (Pakistan)</option>
                      <option value="+971">🇦🇪 +971 (UAE)</option>
                      <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                      <option value="+974">🇶🇦 +974 (Qatar)</option>
                      <option value="+965">🇰🇼 +965 (Kuwait)</option>
                      <option value="+973">🇧🇭 +973 (Bahrain)</option>
                      <option value="+968">🇴🇲 +968 (Oman)</option>
                      <option value="+961">🇱🇧 +961 (Lebanon)</option>
                      <option value="+962">🇯🇴 +962 (Jordan)</option>
                      <option value="+20">🇪🇬 +20 (Egypt)</option>
                      <option value="+27">🇿🇦 +27 (South Africa)</option>
                      <option value="+61">🇦🇺 +61 (Australia)</option>
                      <option value="+64">🇳🇿 +64 (New Zealand)</option>
                      <option value="+86">🇨🇳 +86 (China)</option>
                      <option value="+81">🇯🇵 +81 (Japan)</option>
                      <option value="+82">🇰🇷 +82 (South Korea)</option>
                      <option value="+65">🇸🇬 +65 (Singapore)</option>
                      <option value="+60">🇲🇾 +60 (Malaysia)</option>
                      <option value="+66">🇹🇭 +66 (Thailand)</option>
                      <option value="+62">🇮🇩 +62 (Indonesia)</option>
                      <option value="+63">🇵🇭 +63 (Philippines)</option>
                      <option value="+84">🇻🇳 +84 (Vietnam)</option>
                      <option value="+880">🇧🇩 +880 (Bangladesh)</option>
                      <option value="+94">🇱🇰 +94 (Sri Lanka)</option>
                      <option value="+977">🇳🇵 +977 (Nepal)</option>
                      <option value="+33">🇫🇷 +33 (France)</option>
                      <option value="+49">🇩🇪 +49 (Germany)</option>
                      <option value="+39">🇮🇹 +39 (Italy)</option>
                      <option value="+34">🇪🇸 +34 (Spain)</option>
                      <option value="+31">🇳🇱 +31 (Netherlands)</option>
                      <option value="+32">🇧🇪 +32 (Belgium)</option>
                      <option value="+41">🇨🇭 +41 (Switzerland)</option>
                      <option value="+43">🇦🇹 +43 (Austria)</option>
                      <option value="+46">🇸🇪 +46 (Sweden)</option>
                      <option value="+47">🇳🇴 +47 (Norway)</option>
                      <option value="+45">🇩🇰 +45 (Denmark)</option>
                      <option value="+358">🇫🇮 +358 (Finland)</option>
                      <option value="+7">🇷🇺 +7 (Russia)</option>
                      <option value="+90">🇹🇷 +90 (Turkey)</option>
                      <option value="+52">🇲🇽 +52 (Mexico)</option>
                      <option value="+55">🇧🇷 +55 (Brazil)</option>
                      <option value="+54">🇦🇷 +54 (Argentina)</option>
                      <option value="+57">🇨🇴 +57 (Colombia)</option>
                      <option value="+234">🇳🇬 +234 (Nigeria)</option>
                      <option value="+254">🇰🇪 +254 (Kenya)</option>
                      <option value="+212">🇲🇦 +212 (Morocco)</option>
                      <option value="+972">🇮🇱 +972 (Israel)</option>
                      <option value="+98">🇮🇷 +98 (Iran)</option>
                      <option value="+964">🇮🇶 +964 (Iraq)</option>
                      <option value="+967">🇾🇪 +967 (Yemen)</option>
                      <option value="+963">🇸🇾 +963 (Syria)</option>
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`form-input ${errors.phone ? 'error' : ''}`}
                      placeholder="e.g. 300 1234567"
                      required
                    />
                  </div>
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="license_number">License Number</label>
                  <input
                    type="text"
                    id="license_number"
                    name="license_number"
                    value={formData.license_number}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter driving license number"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter street address"
                  />
                </div>
              </div>

              <div className="form-row three-columns">
                <div className="form-group">
                  <label htmlFor="country">Country *</label>
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    className={`form-input ${errors.country ? 'error' : ''}`}
                    placeholder="Enter country"
                    required
                  />
                  {errors.country && <span className="error-message">{errors.country}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="city">City / Town *</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className={`form-input ${errors.city ? 'error' : ''}`}
                    placeholder="Enter city or town"
                    required
                  />
                  {errors.city && <span className="error-message">{errors.city}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="postalCode">Postal Code *</label>
                  <input
                    type="text"
                    id="postalCode"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    className={`form-input ${errors.postalCode ? 'error' : ''}`}
                    placeholder="Enter postal code"
                    required
                  />
                  {errors.postalCode && <span className="error-message">{errors.postalCode}</span>}
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isSubmitting || uploadingPicture}
                >
                  {uploadingPicture ? '⏳ Uploading Picture...' : isSubmitting ? '⏳ Adding...' : '🚌 Add Driver'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && successData && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000
          }}
          onClick={() => {
            setShowSuccessPopup(false);
            setSuccessData(null);
          }}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              animation: 'slideIn 0.3s ease-out'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Success Icon */}
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 15px -3px rgba(16, 185, 129, 0.3)'
            }}>
              <span style={{ fontSize: '3rem', color: '#FFFFFF' }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              textAlign: 'center',
              color: '#1F2937',
              fontSize: '1.5rem',
              fontWeight: '700',
              marginBottom: '0.5rem'
            }}>
              Driver Added Successfully!
            </h2>

            {/* Subtitle */}
            <p style={{
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.95rem',
              marginBottom: '2rem'
            }}>
              {successData.name} has been added to the system
            </p>

            {/* Credentials Card */}
            <div style={{
              background: '#F9FAFB',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              border: '2px solid #E5E7EB'
            }}>
              <h3 style={{
                color: '#374151',
                fontSize: '1rem',
                fontWeight: '600',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                Login Credentials
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Employee ID */}
                {successData.employeeId && successData.employeeId !== 'N/A' && (
                  <div style={{
                    background: '#FFFFFF',
                    borderRadius: '8px',
                    padding: '1rem',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      fontWeight: '500',
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Employee ID
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      color: '#1F2937',
                      fontWeight: '600',
                      wordBreak: 'break-all'
                    }}>
                      {successData.employeeId}
                    </div>
                  </div>
                )}

                {/* Email */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Email
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    color: '#1F2937',
                    fontWeight: '600',
                    wordBreak: 'break-all'
                  }}>
                    {successData.email}
                  </div>
                </div>

                {/* Password */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Password
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    color: '#1F2937',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    letterSpacing: '1px'
                  }}>
                    {successData.password}
                  </div>
                </div>
              </div>
            </div>

            {/* Info Message */}
            <div style={{
              background: '#EFF6FF',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              border: '1px solid #BFDBFE'
            }}>
              <p style={{
                color: '#1E40AF',
                fontSize: '0.875rem',
                margin: 0,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}>
                <span>ℹ️</span>
                <span>Please share these credentials with the driver</span>
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowSuccessPopup(false);
                setSuccessData(null);
              }}
              style={{
                width: '100%',
                padding: '0.875rem 1.5rem',
                background: '#FF8C42',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 6px -1px rgba(255, 140, 66, 0.3)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#FF7A2E';
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 8px -1px rgba(255, 140, 66, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = '#FF8C42';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 6px -1px rgba(255, 140, 66, 0.3)';
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddTransportPerson;
